import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

const DEFAULT = {
  IMG: require('../../images/common/img_venu_default.png'),
  STYLE: {
    backgroundImage: `url(${require('../../images/common/img_venu_default.png')})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
};

const LazyImg = ({
  isHash,
  className,
  style,
  src,
  defaultImg,
  isBgImg,
  alt,
  title,
  width,
  height,
  key,
  onLoad,
  onClick,
  onError,
  renderChildren,
  dataImageId,
}) => {
  const ref = useRef(null);
  const isRealUser = navigator.userAgent !== 'Funliday-Prerender';
  const isSupportIntersectionObserver = useMemo(() => {
    let isSupport;
    try {
      isSupport = 'IntersectionObserver' in window;
    } catch (error) {
      isSupport = false;
    }
    return isSupport;
  }, []);

  const isLazyLoadOpen = isSupportIntersectionObserver && isRealUser;

  const createImgToLoadBgImg = useCallback(
    (img, observer) => {
      let bgImg = new Image();
      bgImg.src = img.dataset.src;
      bgImg.onload = () => {
        if (!ref.current) return; // onload 時 若 this.El.current 已清空就代表此元件已卸除 此時不繼續執行 否則 setState 會 memory leak
        ref.current.style.backgroundImage = `url(${img.dataset.src})`;
        // 移除 data-src 和監視
        img.removeAttribute('data-src');
        observer.unobserve(img);
        // div 這時才觸發外層傳入自定 onload
        if (typeof onLoad === 'function') {
          onLoad({ width: bgImg.naturalWidth, height: bgImg.naturalHeight });
        }

        // 將 new Image() 指向的變數改為 null ,JS 即會釋放沒用到的 DOM Reference 和 memory
        bgImg = null;
      };

      bgImg.onError = () => {
        ref.current.style.backgroundImage = `url(${DEFAULT.IMG})`;
        // div 這時才觸發外層傳入自定 onError
        if (typeof onError === 'function') {
          onError();
        }
      };
    },
    [src],
  );

  const setImgSrcToLoadImg = useCallback(
    (img, observer) => {
      // img tag
      img.setAttribute('src', img.dataset.src);
      img.addEventListener('load', () => {
        img.setAttribute('style', 'filter:none; transition: filter 0.5s;');
        if (typeof onLoad === 'function') {
          onLoad({ width: img.naturalWidth, height: img.naturalHeight });
        }
      });
      // 移除 data-src 和監視
      // img.removeAttribute('data-src', src);
      observer.unobserve(img);
    },
    [src],
  );

  useEffect(() => {
    if (isLazyLoadOpen) {
      const watcher = new IntersectionObserver((entries, observer) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // 監視圖片標簽進入網頁可視範圍
            const img = entry.target;
            if (isBgImg) {
              createImgToLoadBgImg(img, observer);
            } else {
              setImgSrcToLoadImg(img, observer);
            }
          }
        }
      });
      watcher.observe(ref.current);
    }
  }, [src, defaultImg]);

  if (isBgImg) {
    return (
      <div
        data-src={src}
        data-image-id={dataImageId}
        style={
          isLazyLoadOpen
            ? DEFAULT.STYLE
            : {
                ...DEFAULT.STYLE,
                backgroundImage: `url(${src})`,
              }
        }
        className={className}
        ref={ref}
        key={key}
        onClick={onClick}
        onError={onError}
        title={title}
      >
        {renderChildren ? renderChildren() : null}
      </div>
    );
  }

  return (
    <img
      data-src={src}
      data-image-id={dataImageId}
      src={isLazyLoadOpen ? defaultImg : src}
      alt={alt}
      style={isHash ? { ...style, filter: 'blur(2px)' } : { ...style }}
      title={title}
      width={width}
      height={height}
      className={className}
      ref={ref}
      key={key}
      onLoad={onLoad}
      onClick={onClick}
      onError={onError}
    />
  );
};

LazyImg.defaultProps = {
  isHash: false,
  defaultImg: DEFAULT.IMG,
  style: {},
  isBgImg: false,
  src: null,
  alt: null,
  title: null,
  width: null,
  height: null,
  className: null,
  key: null,
  onLoad: null,
  onClick: null,
  onError: null,
  renderChildren: null,
  dataImageId: null,
};
LazyImg.propTypes = {
  isHash: PropTypes.bool,
  defaultImg: PropTypes.any,
  style: PropTypes.object,
  isBgImg: PropTypes.bool,
  src: PropTypes.string,
  alt: PropTypes.string,
  title: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  className: PropTypes.string,
  key: PropTypes.string,
  onLoad: PropTypes.func,
  onClick: PropTypes.func,
  onError: PropTypes.func,
  renderChildren: PropTypes.func,
  dataImageId: PropTypes.string,
};
export default LazyImg;
